import React, { useCallback, useEffect, useMemo, useState, memo } from 'react';
import { Position } from '@xyflow/react';
import { AppNode, NodeType, NodeStatus } from '@/types';
import { useSelectionStore } from '@/stores/selectionStore';
import { useReferenceStore } from '@/stores/referenceStore';
import { useNodeStore } from '@/stores/nodeStore';
import { toast } from 'sonner';
import { NODE_TYPE_CONFIG } from '@/config/nodeTypeConfig';
import { NodeTitle } from './parts/NodeTitle';
import { TopFunctionBar } from './parts/TopFunctionBar';
import { UploadButton } from './parts/UploadButton';
import { TryActions } from './parts/TryActions';
import { EditorPanel } from './parts/EditorPanel';
import { MagneticHandle } from './parts/MagneticHandle';
import { InverseScaleBox } from './parts/InverseScaleBox';
import { FEATURE_GRIDS } from './parts/GridOverlay';
import { PanoramaPanel, PanoramaToolbar } from './parts/PanoramaPanel';
import { FeatureConfirmBar } from './parts/FeatureConfirmBar';
import { GridSplitBar } from './parts/GridSplitBar';
import { PanoramaContent, MediaUploadContent, AudioContent, GeneratorContent, SelfWriteContent } from './parts/CardContentRenderer';
import { FloatingToolbar } from './parts/FloatingToolbar';
import { useInputAssets, useGenerateNodeActions, useNodeMedia, useNodeInteraction, useSelfWriteEditor } from './hooks';

const DEFAULT_NODE_WIDTH = 420;
const DEFAULT_ASPECT_RATIO = '16:9';
const SPRING = "cubic-bezier(0.32, 0.72, 0, 1)";

const isUploadNode = (nodeType: NodeType) => nodeType === NodeType.MEDIA_UPLOAD;

interface GenerateNodeProps {
  data: AppNode;
  selected: boolean;
  dragging?: boolean;
  onNodeAction: (id: string, prompt?: string) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  onNodeDelete: (id: string) => void;
  onExtractImage?: (nodeId: string, image: string) => void;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  onEnterPanorama?: (nodeId: string, imageUrl: string) => void;
  onGeneratePanorama?: (sourceNodeId: string, params: {
    prompt: string;
    quality: import('@/types').PanoramaQuality;
    referenceImageUrl?: string;
    mode: 'txt2panorama' | 'img2panorama';
    model?: string;
  }) => void;
  onToggleChat?: () => void;
  onOpenChat?: () => void;
}

const GenerateNodeComponent: React.FC<GenerateNodeProps> = memo(({
  data,
  selected,
  dragging = false,
  onNodeAction,
  onNodeUpdate,
  onNodeDelete,
  onExtractImage,
  onAddNode,
  onEnterPanorama,
  onGeneratePanorama,
  onToggleChat,
  onOpenChat,
}) => {
  const config = NODE_TYPE_CONFIG[data.type] || NODE_TYPE_CONFIG[NodeType.IMAGE_GENERATOR];
  const IconComponent = config.icon;
  const isWorking = data.status === NodeStatus.WORKING;
  const hasError = data.status === NodeStatus.ERROR;

  const [localPrompt, setLocalPrompt] = useState(data.data.prompt || '');

  const {
    onNodeUpdateRef,
    commitPrompt,
    handleGenerate,
    handlePromptChange,
    handlePromptSubmit,
    handleImageModeSwitch,
    handleAddUploadNodeForPanorama,
    handleTryAction,
    currentImageMode,
  } = useGenerateNodeActions({
    data,
    localPrompt,
    onNodeAction,
    onNodeUpdate,
    onAddNode: useCallback((node: AppNode, position: { x: number; y: number }, options?: { connectToNodeId?: string }) => {
      useNodeStore.getState().addNode(node);
      if (options?.connectToNodeId) {
        useNodeStore.getState().addConnection(node.id, options.connectToNodeId);
      }
    }, [onAddNode]),
    onSetLocalPrompt: setLocalPrompt,
  });

  const {
    isPlayingAudio,
    setIsPlayingAudio,
    isPlayingVideo,
    videoProgress,
    videoDuration,
    showImageGrid,
    mediaRef,
    handleMouseEnter,
    handleMouseLeave,
    toggleVideoPlay,
    handleVideoTimeUpdate,
    handleVideoSeek,
    handleVideoEnded,
    handleVideoLoadedMetadata,
  } = useNodeMedia({ data });

  const {
    isHovered,
    setIsHovered,
    isInputFocused,
    isCtrlHeld,
    activeFeature,
    gridSplit,
    showPanoramaPanel,
    chargeComplete,
    selectedCells,
    setSelectedCells,
    handleFeatureSelect,
    handlePanoramaClick,
    handleGridSplitClose,
    handleFeatureClose,
    handlePanoramaClose,
  } = useNodeInteraction({ data });

  const selfWriteResult = useSelfWriteEditor({
    data,
    onNodeUpdate,
  });

  // P1-4: 改为订阅布尔值，避免每次选中数量变化都重渲染所有 GenerateNode
  // 之前订阅 selectedNodeIds.size（数字），任何选中变化都触发重渲染
  // 现在只有"是否多选"状态变化（0/1 → 2+ 或 2+ → 0/1）才触发重渲染
  const isMultiSelectedState = useSelectionStore((s) => s.selectedNodeIds.size > 1);
  const isBoxSelecting = useSelectionStore((s) => s.isBoxSelecting);
  const isMultiSelected = selected && (isMultiSelectedState || isBoxSelecting);

  useEffect(() => { setLocalPrompt(data.data.prompt || ''); }, [data.data.prompt]);

  // 文本节点自己编写模式
  const isSelfWrite = data.type === NodeType.PROMPT_INPUT && data.data.subMode === 'self-write';

  const inputAssets = useInputAssets(data.inputs);

  const cardHeight = useMemo(() => {
    if (isSelfWrite) return data.data.nodeSize?.height || 220;
    if (data.height) return data.height;
    const ratio = data.data.aspectRatio || DEFAULT_ASPECT_RATIO;
    const [w, h] = ratio.split(':').map(Number);
    return (data.width || DEFAULT_NODE_WIDTH) * h / w;
  }, [data.height, data.width, data.data.aspectRatio, data.data.nodeSize, isSelfWrite]);

  const nodeWidth = isSelfWrite ? (data.data.nodeSize?.width || 320) : (data.width || DEFAULT_NODE_WIDTH);
  const hasMedia = !!(
    data.data.image ||
    (data.data.images && data.data.images.length > 0) ||
    data.data.videoUri ||
    (data.data.videoUris && data.data.videoUris.length > 0) ||
    data.data.audioUri ||
    data.data.panoramaOutputUrl
  );
  const isOpen = isHovered || isInputFocused || selected;
  const uploadNode = isUploadNode(data.type);
  const mediaType = data.data.image || (data.data.images && data.data.images.length > 0)
    ? 'image'
    : data.data.videoUri || (data.data.videoUris && data.data.videoUris.length > 0)
      ? 'video'
      : data.data.audioUri
        ? 'audio'
        : data.data.panoramaOutputUrl
          ? 'image'
          : undefined;
  const mediaUrl = data.data.image ||
    (data.data.images && data.data.images[0]) ||
    data.data.videoUri ||
    (data.data.videoUris && data.data.videoUris[0]) ||
    data.data.audioUri ||
    data.data.panoramaOutputUrl;
  const activeFeatureGrid = activeFeature ? FEATURE_GRIDS[activeFeature.label] : null;

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
    handleMouseEnter();
  }, [handleMouseEnter, setIsHovered]);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
    handleMouseLeave();
  }, [handleMouseLeave, setIsHovered]);

  const renderCardContent = () => {
    if (isSelfWrite) {
      return (
        <SelfWriteContent
          data={data}
          selected={selected}
          editMode={selfWriteResult.editMode}
          editor={selfWriteResult.editor}
          onEnterEdit={selfWriteResult.enterEdit}
          onEnterFullscreen={selfWriteResult.enterFullscreen}
          onExitToPreview={selfWriteResult.exitToPreview}
          onExitToEdit={selfWriteResult.exitToEdit}
          onResize={selfWriteResult.handleResize}
        />
      );
    }
    if (data.type === NodeType.MEDIA_UPLOAD) {
      return (
        <MediaUploadContent
          data={data}
          hasMedia={hasMedia}
          gridSplit={gridSplit}
          activeFeatureGrid={activeFeatureGrid}
          selectedCells={selectedCells}
          onSelectedCellsChange={setSelectedCells}
          mediaRef={mediaRef}
          isPlayingAudio={isPlayingAudio}
          setIsPlayingAudio={setIsPlayingAudio}
          isPlayingVideo={isPlayingVideo}
          videoProgress={videoProgress}
          videoDuration={videoDuration}
          onToggleVideoPlay={toggleVideoPlay}
          onVideoTimeUpdate={handleVideoTimeUpdate}
          onVideoSeek={handleVideoSeek}
          onVideoEnded={handleVideoEnded}
          onVideoLoadedMetadata={handleVideoLoadedMetadata}
        />
      );
    }
    if (data.type === NodeType.AUDIO_GENERATOR) {
      return (
        <AudioContent
          data={data}
          isWorking={isWorking}
          hasError={hasError}
          mediaRef={mediaRef}
          isPlayingAudio={isPlayingAudio}
          setIsPlayingAudio={setIsPlayingAudio}
        />
      );
    }
    if (data.type === NodeType.PANORAMA_GENERATOR) {
      return (
        <PanoramaContent
          data={data}
          isWorking={isWorking}
          hasError={hasError}
          onNodeUpdate={onNodeUpdate}
        />
      );
    }
    return (
      <GeneratorContent
        data={data}
        isWorking={isWorking}
        hasError={hasError}
        hasMedia={hasMedia}
        showImageGrid={showImageGrid}
        gridSplit={gridSplit}
        activeFeatureGrid={activeFeatureGrid}
        selectedCells={selectedCells}
        onSelectedCellsChange={setSelectedCells}
        onNodeUpdate={onNodeUpdate}
        mediaRef={mediaRef}
        IconComponent={IconComponent}
        isPlayingVideo={isPlayingVideo}
        videoProgress={videoProgress}
        videoDuration={videoDuration}
        onToggleVideoPlay={toggleVideoPlay}
        onVideoTimeUpdate={handleVideoTimeUpdate}
        onVideoSeek={handleVideoSeek}
        onVideoEnded={handleVideoEnded}
        onVideoLoadedMetadata={handleVideoLoadedMetadata}
      />
    );
  };

  return (
    <div
      className="relative"
      style={{
        width: nodeWidth,
        transformOrigin: 'center center',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDoubleClick={(e) => {
        e.stopPropagation();
      }}
      onContextMenu={(e) => e.stopPropagation()}
    >
      {isSelfWrite && selfWriteResult.editMode === 'edit' && selfWriteResult.editor && (
        <InverseScaleBox
          className="absolute -top-[76px] left-1/2 flex justify-center z-30 transition-transform duration-300"
          style={{ transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
          scaleOrigin="center bottom"
        >
          <FloatingToolbar
            editor={selfWriteResult.editor}
            onFullscreen={selfWriteResult.enterFullscreen}
            showWordCount
          />
        </InverseScaleBox>
      )}

      {selected && !dragging && !isMultiSelected && !activeFeature && !gridSplit && !showPanoramaPanel && !isSelfWrite && (
        <InverseScaleBox
          className="absolute -top-[76px] left-1/2 flex justify-center z-30 transition-transform duration-300"
          style={{ transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
          scaleOrigin="center bottom"
        >
          {data.type === NodeType.PANORAMA_GENERATOR && data.data.isPanoramaPreviewOpen && data.data.panoramaOutputUrl ? (
            <PanoramaToolbar data={data} onNodeUpdate={onNodeUpdate} />
          ) : hasMedia ? (
            <TopFunctionBar
              nodeId={data.id}
              nodeType={data.type}
              status={data.status}
              hasMedia={hasMedia}
              mediaUrl={mediaUrl}
              mediaType={mediaType}
              images={data.data.images}
              hasInputAssets={inputAssets.length > 0}
              onUpdate={onNodeUpdate}
              onExtractImage={onExtractImage}
              onDelete={onNodeDelete}
              onFeatureSelect={handleFeatureSelect}
              onEnterPanorama={handlePanoramaClick}
            />
          ) : (
            <UploadButton
              nodeId={data.id}
              nodeType={data.type}
              onUpdate={onNodeUpdate}
            />
          )}
        </InverseScaleBox>
      )}

      {selected && !dragging && !isMultiSelected && showPanoramaPanel && (
        <PanoramaPanel
          data={data}
          cardHeight={cardHeight}
          onNodeUpdate={onNodeUpdate}
          onGeneratePanorama={onGeneratePanorama}
          onClose={handlePanoramaClose}
        />
      )}

      {selected && !dragging && !isMultiSelected && gridSplit && (
        <GridSplitBar
          data={data}
          cardHeight={cardHeight}
          gridSplit={gridSplit}
          selectedCells={selectedCells}
          onAddNode={onAddNode}
          onClose={handleGridSplitClose}
        />
      )}

      {/* 节点标题 */}
      <div
        className="absolute -top-6 left-0 z-10"
        style={{ maxWidth: '50%' }}
      >
        <NodeTitle
          nodeId={data.id}
          title={data.title}
          type={data.type}
          onUpdate={onNodeUpdate}
        />
      </div>

      <div
        data-node-card={data.id}
        className={`group relative overflow-hidden transition-[border-color,box-shadow,background-color] duration-300 rounded-[16px] border ${
          isWorking && !isSelfWrite ? 'charge-border' :
          selected && !isMultiSelected
            ? 'ring-2 ring-white/50 shadow-[0_0_40px_-10px_rgba(255,255,255,0.3)] border-white/40'
            : isMultiSelected
              ? 'border-transparent'
              : 'border-white/25 hover:border-white/40'
        } ${data.type === NodeType.PANORAMA_GENERATOR && data.data.isPanoramaPreviewOpen && !isCtrlHeld ? 'nodrag nowheel' : ''} ${isSelfWrite && selfWriteResult.editMode === 'edit' ? 'nodrag nowheel' : ''}`}
        style={{
          width: nodeWidth,
          height: cardHeight,
          // backdrop-filter: blur(24px) 强制浏览器为每个节点创建独立 GPU 合成层
          // ReactFlow 的 inline transform: translate(Xpx,Ypx) 会覆盖 CSS 的 translateZ(0)，
          // 所以 translateZ(0) 不生效。backdrop-filter 是唯一能强制创建合成层的机制。
          // 有合成层时平移=GPU移动纹理(极廉价)，无合成层时平移=CPU重绘(节点多时卡顿)
          background: isSelfWrite ? '#1a1a1a' : (selected ? 'rgba(28, 28, 30, 0.85)' : 'rgba(28, 28, 30, 0.6)'),
          backdropFilter: isSelfWrite ? 'none' : 'blur(24px)',
        }}
      >
        {hasMedia && !isSelfWrite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              useReferenceStore.getState().addReference(data);
              toast.success('已引用为参考');
              onOpenChat?.();
            }}
            className={`absolute top-2 right-2 z-[100] flex items-center justify-center w-7 h-7 rounded-lg bg-black/60 backdrop-blur-sm border border-white/20 text-white/80 hover:text-white hover:bg-white/20 transition-all ${isHovered || selected ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            title="引用为参考"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
          </button>
        )}
        <div className="w-full h-full relative">
          {renderCardContent()}
        </div>

        {(isWorking || chargeComplete) && !isSelfWrite && (
          <div className="charge-overlay">
            {isWorking && <div className="charge-scan-line" />}
            {chargeComplete && <div className="charge-complete-flash" />}
          </div>
        )}

        {!hasMedia && !uploadNode && inputAssets.length === 0 && !isSelfWrite && (
          <TryActions
            nodeId={data.id}
            nodeType={data.type}
            onTryAction={handleTryAction}
          />
        )}
      </div>

      {selected && !uploadNode && !data.data.isManualUpload && !dragging && !isMultiSelected && !activeFeature && !gridSplit && !showPanoramaPanel && !isSelfWrite && (
        <InverseScaleBox
          className="absolute left-1/2 z-20 transition-all duration-500"
          style={{
            top: cardHeight + 12,
            width: data.type === NodeType.IMAGE_GENERATOR && currentImageMode === 'UPSCALE' ? 420 : 620,
            opacity: 1,
            transform: 'translateX(-50%) translateY(0)',
            pointerEvents: 'auto',
            transitionTimingFunction: SPRING,
            transformOrigin: 'top center',
          }}
          scaleOrigin="top center"
        >
          <EditorPanel
            nodeId={data.id}
            nodeType={data.type}
            status={data.status}
            prompt={localPrompt}
            model={data.data.model}
            aspectRatio={data.data.aspectRatio}
            resolution={data.data.resolution}
            duration={data.data.duration}
            imageCount={data.data.imageCount}
            videoCount={data.data.videoCount}
            upscaleStrength={data.data.upscaleStrength}
            generateAudio={data.data.generateAudio}
            inputAssets={inputAssets}
            isOpen={isOpen}
            currentImageMode={currentImageMode}
            onPromptChange={handlePromptChange}
            onPromptSubmit={handlePromptSubmit}
            onUpdate={onNodeUpdate}
            onGenerate={handleGenerate}
            onImageModeSwitch={handleImageModeSwitch}
            onAddUploadNode={data.type === NodeType.PANORAMA_GENERATOR ? handleAddUploadNodeForPanorama : undefined}
          />
        </InverseScaleBox>
      )}

      {activeFeature && (
        <FeatureConfirmBar
          data={data}
          cardHeight={cardHeight}
          activeFeature={activeFeature}
          onNodeUpdate={onNodeUpdate}
          onNodeAction={onNodeAction}
          onAddNode={onAddNode}
          onClose={handleFeatureClose}
        />
      )}

      <MagneticHandle
        type="target"
        position={Position.Left}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={nodeWidth}
      />

      <MagneticHandle
        type="source"
        position={Position.Right}
        nodeId={data.id}
        cardHeight={cardHeight}
        cardWidth={nodeWidth}
      />
    </div>
  );
});

export const GenerateNode = memo(GenerateNodeComponent, (prevProps, nextProps) => {
  const pd = prevProps.data?.data;
  const nd = nextProps.data?.data;
  return (
    prevProps.data?.id === nextProps.data?.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.dragging === nextProps.dragging &&
    prevProps.data?.status === nextProps.data?.status &&
    prevProps.data?.width === nextProps.data?.width &&
    prevProps.data?.height === nextProps.data?.height &&
    pd?.model === nd?.model &&
    pd?.image === nd?.image &&
    pd?.videoUri === nd?.videoUri &&
    pd?.audioUri === nd?.audioUri &&
    pd?.error === nd?.error &&
    pd?.progress === nd?.progress &&
    pd?.prompt === nd?.prompt &&
    pd?.generationMode === nd?.generationMode &&
    pd?.panoramaOutputUrl === nd?.panoramaOutputUrl &&
    pd?.isPanoramaPreviewOpen === nd?.isPanoramaPreviewOpen &&
    pd?.images === nd?.images &&
    pd?.videoUris === nd?.videoUris &&
    pd?.aspectRatio === nd?.aspectRatio &&
    pd?.resolution === nd?.resolution &&
    pd?.duration === nd?.duration &&
    pd?.imageCount === nd?.imageCount &&
    pd?.videoCount === nd?.videoCount &&
    pd?.upscaleStrength === nd?.upscaleStrength &&
    pd?.generateAudio === nd?.generateAudio &&
    pd?.isManualUpload === nd?.isManualUpload &&
    pd?.subMode === nd?.subMode &&
    pd?.editMode === nd?.editMode &&
    pd?.nodeSize === nd?.nodeSize &&
    pd?.richContent === nd?.richContent &&
    pd?.textContent === nd?.textContent &&
    prevProps.data?.inputs?.length === nextProps.data?.inputs?.length
  );
});
